import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Scissors, 
  ZoomIn, ZoomOut, Download, Music, Film, Trash2, Copy, Zap,
  ChevronLeft, ChevronRight, RotateCcw, Minus, Plus, AlertTriangle, Check, Loader2,
  Clapperboard, Square } from 'lucide-react';
import { toast } from '../../utils/toast';
// Diagnostic mode: enable with `?debug=1` on the URL. Renders a compact
// inline badge on each clip showing trimStart / trimEnd / originalDuration /
// imgLeft / imgWidth so we can verify exactly what the client is rendering.
const DEBUG = (typeof window !== 'undefined')
  && new URLSearchParams(window.location.search).get('debug') === '1';
if (typeof window !== 'undefined' && DEBUG && !window.__trimLog) window.__trimLog = [];
const logTrim = (op, id, val) => {
  if (!DEBUG || typeof window === 'undefined') return;
  const entry = { t: new Date().toISOString().slice(11, 23), op, id: String(id).slice(-6), val: Number(val).toFixed(3) };
  window.__trimLog.push(entry);
  if (window.__trimLog.length > 20) window.__trimLog.shift();
  // eslint-disable-next-line no-console
  console.log('[TRIM]', entry);
};

const TRANSITION_DURATION = 0.4; // seconds for crossfade between clips

const PPS_DEFAULT = 120;          // pixel-density reference used by the `%` label
const PPS_INITIAL = 360;          // default zoom = 300% (3 × un-stretched baseline)
const PPS_MIN = 10;
const PPS_MAX = 720;              // 600% ceiling — plenty of headroom above the new default
const FPS = 30;
const TRACK_H = 64;

function fmt(s) {
  const m = Math.floor(s / 60), sec = Math.floor(s % 60), f = Math.floor((s % 1) * FPS);
  return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}:${String(f).padStart(2,'0')}`;
}

function Ruler({ total, pps, pos, onScrub, playheadColor = '#38BDF8' }) {
  const marks = [];
  const step = pps >= 30 ? 1 : pps >= 15 ? 2 : 5;
  for (let t = 0; t <= total; t += step) {
    marks.push(
      <div key={t} className="absolute top-0" style={{ left: t * pps }}>
        <div className="w-px h-3" style={{ background: 'rgba(139,92,246,0.4)' }} />
        {t % (step*2) === 0 && <span className="absolute top-3 text-[9px] text-white/40 select-none -translate-x-1/2">{fmt(t).slice(0,5)}</span>}
      </div>
    );
  }
  
  const startDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const parent = e.currentTarget.closest('[data-testid="timeline-track-area"]') || e.currentTarget.parentElement;

    // Edge-pan state: continuously scroll the container while the pointer hovers
    // within EDGE_PX of either viewport edge. Splice / Premiere-style behaviour.
    const EDGE_PX = 60;   // threshold from the edge
    const MAX_SPEED = 28; // px per RAF tick at the extreme edge
    let lastClientX = 0;
    let panRAF = null;

    const scrubAt = (clientX) => {
      const rect = parent.getBoundingClientRect();
      const x = clientX - rect.left + parent.scrollLeft;
      const raw = Math.max(0, Math.min(x / pps, total));
      const t = Math.round(raw * FPS) / FPS;
      onScrub(t);
    };

    const edgePanTick = () => {
      const rect = parent.getBoundingClientRect();
      let delta = 0;
      if (lastClientX > rect.right - EDGE_PX) {
        const over = Math.min(EDGE_PX, lastClientX - (rect.right - EDGE_PX));
        delta = Math.round((over / EDGE_PX) * MAX_SPEED);
      } else if (lastClientX < rect.left + EDGE_PX) {
        const over = Math.min(EDGE_PX, (rect.left + EDGE_PX) - lastClientX);
        delta = -Math.round((over / EDGE_PX) * MAX_SPEED);
      }
      if (delta !== 0) {
        const max = parent.scrollWidth - parent.clientWidth;
        const before = parent.scrollLeft;
        const next = Math.max(0, Math.min(max, before + delta));
        if (next !== before) {
          parent.scrollLeft = next;
          // Re-scrub so the playhead tracks the finger as the viewport pans
          scrubAt(lastClientX);
        }
      }
      panRAF = requestAnimationFrame(edgePanTick);
    };

    const readClientX = (ev) => {
      const isTouch = ev.touches && ev.touches.length > 0;
      lastClientX = isTouch ? ev.touches[0].clientX : ev.clientX;
    };

    readClientX(e);
    scrubAt(lastClientX);
    panRAF = requestAnimationFrame(edgePanTick);

    const onMove = (ev) => {
      ev.preventDefault();
      readClientX(ev);
      scrubAt(lastClientX);
    };
    const onUp = () => {
      if (panRAF) cancelAnimationFrame(panRAF);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  };
  
  // Glow for Splice-yellow, plain line for default
  const isYellow = playheadColor === '#FBBF24';
  
  return (
    <div className="relative h-6 border-b border-ai-border cursor-pointer" 
      style={{ width: total * pps, minWidth: '100%' }}
      onMouseDown={startDrag}
      onTouchStart={startDrag}>
      {marks}
      {/* Playhead */}
      <div className="absolute top-0 bottom-0 z-30 pointer-events-none" style={{ 
        left: pos * pps - 0.5,
        width: isYellow ? 2 : 1,
        background: playheadColor,
        boxShadow: isYellow ? `0 0 6px ${playheadColor}, 0 0 14px ${playheadColor}90` : undefined,
      }}>
        <div className="absolute -top-0.5 -left-2 w-4 h-3.5 cursor-grab pointer-events-auto"
          style={{ background: playheadColor, borderRadius: '0 0 4px 4px' }}
          onMouseDown={(e) => { e.stopPropagation(); startDrag(e); }}
          onTouchStart={(e) => { e.stopPropagation(); startDrag(e); }} />
      </div>
    </div>
  );
}

function Clip({ clip, pps, isSelected, isPlaying, onSelect, onSeekTap, onRemove, onDragStart, onTrimStart, onTrimEnd, onDragEnd, scrubTime, variant, token, apiBase }) {
  const w = clip.duration * pps;
  const l = clip.startTime * pps;
  const dragging = useRef(false);
  const vidRef = useRef(null);
  const rootRef = useRef(null);
  const filmstripImgRef = useRef(null);
  const showFilmstrip = variant === 'splice' && !!(clip.videoUrl || clip.videoS3Url);
  const [filmstripUrl, setFilmstripUrl] = useState(null);

  // Fetch the server-generated filmstrip image once per clip (cached on backend)
  useEffect(() => {
    if (!showFilmstrip || !token || !apiBase) return;
    let cancelled = false;
    const rawUrl = clip.videoUrl || clip.videoS3Url;
    // Strip our origin + ?token= so the backend hashes the same key regardless of caller
    let clean = rawUrl;
    try {
      const u = new URL(rawUrl, apiBase);
      if (u.origin === apiBase || u.pathname.startsWith('/api/')) clean = u.pathname;
    } catch (_) { /* leave as-is */ }
    // Let the backend compute frame count adaptively from the video's probed duration
    // (<5s→12, 5–15s→20, ≥15s→30) at 72px tall with aspect-preserving scaling.
    // This gives enough horizontal pixel density to survive the 1.5–3× upscaling
    // that trim-drag applies to the img.
    fetch(`${apiBase}/api/recipes/timeline/filmstrip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ video_url: clean }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (cancelled || !j?.strip_url) return;
        const withTok = j.strip_url.includes('?')
          ? `${apiBase}${j.strip_url}&token=${encodeURIComponent(token)}`
          : `${apiBase}${j.strip_url}?token=${encodeURIComponent(token)}`;
        setFilmstripUrl(withTok);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showFilmstrip, clip.videoUrl, clip.videoS3Url, token, apiBase]);

  // Seek the internal <video> to match the global playhead when it's inside this clip's range
  useEffect(() => {
    if (!showFilmstrip || filmstripUrl) return;  // filmstrip image takes over once available
    const v = vidRef.current;
    if (!v) return;
    const clipStart = clip.startTime;
    const clipEnd = clip.startTime + clip.duration;
    let target;
    if (scrubTime < clipStart) target = clip.trimStart || 0;
    else if (scrubTime > clipEnd) target = (clip.trimStart || 0) + clip.duration - 0.001;
    else target = (clip.trimStart || 0) + (scrubTime - clipStart);
    if (v.readyState >= 1 && Math.abs((v.currentTime || 0) - target) > (1 / 30)) {
      try { v.currentTime = target; } catch (_) {}
    }
  }, [scrubTime, clip.startTime, clip.duration, clip.trimStart, showFilmstrip, filmstripUrl]);

  // RAF-coalesced, imperative drag for trim handles.
  // --------------------------------------------------
  // During drag we DO NOT call setState. All visual updates are
  // direct DOM mutations on this clip's root + filmstrip + duration pill,
  // plus `transform: translateX(...)` on subsequent sibling clips (ripple).
  // The live-trim state lives only in `pendingRef`; React state is committed
  // exactly once on pointerup via onTrimStart / onTrimEnd.
  const startTrim = (e, side) => {
    e.stopPropagation();
    e.preventDefault();
    const rootEl = rootRef.current;
    if (!rootEl) return;

    dragging.current = true;
    onDragStart();

    const isTouch = e.type === 'touchstart';
    const x0 = isTouch ? e.touches[0].clientX : e.clientX;
    const d0 = clip.duration;
    const ts0 = clip.trimStart || 0;
    const te0 = clip.trimEnd != null ? clip.trimEnd : (clip.originalDuration || d0);
    const orig = clip.originalDuration || d0;
    const origLeftPx = clip.startTime * pps;

    // Collect subsequent clip DOM nodes once (for ripple translateX)
    const siblings = [];
    let n = rootEl.nextElementSibling;
    while (n) {
      const tid = n.getAttribute && n.getAttribute('data-testid');
      if (tid && tid.startsWith('timeline-clip-')) siblings.push(n);
      n = n.nextElementSibling;
    }

    const pillEl = rootEl.querySelector(`[data-testid="clip-duration-pill-${clip.id}"]`);
    const imgEl = filmstripImgRef.current; // null when no filmstrip
    const internalVid = vidRef.current;    // null when filmstrip is loaded

    // Cache per-frame pointer X and last-committed live values
    const state = {
      clientX: x0,
      nts: ts0,
      nte: te0,
      newDur: d0,
      dirty: false,
      everDirty: false,
    };
    let rafId = null;

    const apply = () => {
      if (!state.dirty) {
        rafId = requestAnimationFrame(apply);
        return;
      }
      state.dirty = false;

      const { nts, nte, newDur } = state;
      const widthPx = Math.max(24, newDur * pps);
      const delta = newDur - d0; // >0 means clip grew
      const deltaPx = delta * pps;
      // shiftPx: how far this clip's LEFT edge should move to track the L-handle.
      // Only nonzero on L drags. On R drags, left edge is anchored.
      const shiftPx = side === 'L' ? (nts - ts0) * pps : 0;

      // 1) Clip container. Side-specific anchoring so the handle being dragged
      //    visually tracks the finger:
      //      L drag → anchor RIGHT edge, move container.left rightward.
      //      R drag → anchor LEFT edge, shrink width from the right.
      rootEl.style.width = `${widthPx}px`;
      rootEl.style.left = `${origLeftPx + shiftPx}px`;

      // 2) Filmstrip image — VIEWPORT model with side-specific rules:
      //   - LEFT handle: trimStart changes → shift img.left by -(nts × pps).
      //   - RIGHT handle: trimStart UNCHANGED → img position must not move at all.
      //     On a tail trim, the filmstrip content is pinned. Frames slide off
      //     the right edge via container `overflow:hidden`. Explicitly DO NOT
      //     touch imgEl during R-drags — React's committed styles remain
      //     authoritative throughout.
      if (imgEl && side === 'L') {
        imgEl.style.width = `${Math.max(1, orig * pps)}px`;
        imgEl.style.left = `${-nts * pps}px`;
      }

      // 3) Duration pill text
      if (pillEl) {
        pillEl.textContent = `:${newDur.toFixed(2).padStart(5, '0')}`;
      }

      // 4) Sibling ripple — only for R drag.
      //    On L drag, our right edge is anchored so siblings stay put during drag;
      //    they snap to new positions once React commits trimStart.
      if (side === 'R') {
        for (let i = 0; i < siblings.length; i++) {
          siblings[i].style.transform = `translateX(${deltaPx}px)`;
          siblings[i].style.willChange = 'transform';
        }
      }

      // 5) Live preview seek on internal video element (best-effort, non-blocking)
      if (internalVid && internalVid.readyState >= 1) {
        const target = side === 'L' ? nts : Math.max(0, nte - 0.01);
        try {
          if (Math.abs((internalVid.currentTime || 0) - target) > (1 / 60)) {
            internalVid.currentTime = target;
          }
        } catch (_) { /* ignore seek errors */ }
      }

      rafId = requestAnimationFrame(apply);
    };

    const onMove = (ev) => {
      // NOTE: no setState, no preventDefault. touch-action:none on the handle prevents scroll.
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      state.clientX = cx;
      const dx = cx - x0;
      const dt = dx / pps;
      if (side === 'L') {
        const nts = Math.max(0, Math.min(ts0 + dt, te0 - 0.5));
        state.nts = nts;
        state.newDur = Math.max(0.5, te0 - nts);
      } else {
        const nte = Math.min(orig, Math.max(ts0 + 0.5, te0 + dt));
        state.nte = nte;
        state.newDur = Math.max(0.5, nte - ts0);
      }
      state.dirty = true;
      state.everDirty = true;
    };

    const cleanup = () => {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      window.removeEventListener('touchcancel', onUp);
      // Explicitly reset rootEl.style.left in case we shifted it on an L drag.
      // React's style-prop diff won't re-apply an unchanged `left` value, so our
      // imperative override would otherwise persist after commit. Reset to the
      // authoritative original; commit's React render will then take over.
      rootEl.style.left = `${origLeftPx}px`;
      // Clear inline styles on siblings so React's next render owns them.
      // The root/filmstrip/pill styles are deliberately NOT cleared here —
      // onUp will either overwrite them via state commit (React re-render)
      // or restore authoritative originals in the no-change path.
      for (let i = 0; i < siblings.length; i++) {
        siblings[i].style.transform = '';
        siblings[i].style.willChange = '';
      }
    };

    const onUp = () => {
      // Commit final value to React state EXACTLY ONCE.
      const finalSide = side;
      const finalVal = finalSide === 'L' ? state.nts : state.nte;
      const changed = finalSide === 'L'
        ? Math.abs(finalVal - ts0) > 1e-4
        : Math.abs(finalVal - te0) > 1e-4;
      cleanup();
      if (changed) {
        // React will re-render with the new geometry and own the DOM again.
        // Our inline width/left/img styles will be overwritten by React's diff.
        if (finalSide === 'L') onTrimStart(clip.id, finalVal);
        else onTrimEnd(clip.id, finalVal);
      } else if (state.everDirty === false) {
        // Mouse released without any pointer movement → nothing to restore.
      } else {
        // We mutated DOM but final value didn't change → restore authoritative
        // originals since React won't re-render.
        rootEl.style.width = `${Math.max(24, d0 * pps)}px`;
        rootEl.style.left = `${origLeftPx}px`;
        if (imgEl) {
          imgEl.style.width = `${Math.max(1, orig * pps)}px`;
          imgEl.style.left = `${-ts0 * pps}px`;
        }
        if (pillEl) pillEl.textContent = `:${d0.toFixed(2).padStart(5, '0')}`;
      }
      // Keep dragging flag briefly so the click-to-select handler doesn't fire
      setTimeout(() => { dragging.current = false; onDragEnd(); }, 80);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    window.addEventListener('touchcancel', onUp);
    // Kick off the RAF loop
    rafId = requestAnimationFrame(apply);
  };

  return (
    <div
      ref={rootRef}
      data-testid={`timeline-clip-${clip.id}`}
      className={`absolute top-1 group cursor-pointer rounded-lg overflow-hidden transition-shadow ${
        isSelected ? 'ring-2 ring-ai-accent shadow-lg shadow-ai-accent/30' 
        : isPlaying ? 'ring-2 ring-pink-400/70 shadow-lg shadow-pink-500/40' 
        : 'hover:ring-1 hover:ring-ai-accent/50'
      }`}
      style={{ left: l, width: Math.max(w, 24), height: TRACK_H - 8 }}
      onClick={(e) => {
        e.stopPropagation();
        if (dragging.current) return;
        onSelect(clip.id);
        // Also jump the playhead to the tapped position within the clip.
        if (onSeekTap && rootRef.current) {
          const rect = rootRef.current.getBoundingClientRect();
          const localX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
          const localSec = localX / pps;
          onSeekTap(clip.startTime + localSec);
        }
      }}
    >
      <div className="absolute inset-0 overflow-hidden" style={{ background: variant === 'splice' ? 'rgba(0,0,0,0.15)' : 'linear-gradient(135deg, rgba(99,60,180,0.6), rgba(139,92,246,0.4))' }}>
        {filmstripUrl ? (
          // VIEWPORT model (not zoom). The filmstrip img stays at a fixed pixel
          // width tied to pps — each second of source video always occupies the
          // same on-screen width, regardless of trim. The clip container is a
          // viewport (overflow: hidden) onto this fixed ribbon. Trimming the head
          // shifts the ribbon left by trimStart*pps px — frames slide off the edge,
          // they do NOT resize.
          (() => {
            const orig = clip.originalDuration || clip.duration;
            const imgWidthPx = Math.max(1, orig * pps);
            const imgLeftPx = -(clip.trimStart || 0) * pps;
            return (
              <img
                ref={filmstripImgRef}
                src={filmstripUrl}
                alt=""
                style={{
                  position: 'absolute',
                  top: 0,
                  height: '100%',
                  width: `${imgWidthPx}px`,
                  maxWidth: 'none',
                  left: `${imgLeftPx}px`,
                  objectFit: 'fill',
                  imageRendering: 'auto',
                  pointerEvents: 'none',
                }}
                data-testid={`clip-filmstrip-${clip.id}`}
              />
            );
          })()
        ) : showFilmstrip ? (
          <video
            ref={vidRef}
            src={clip.videoUrl || clip.videoS3Url}
            className="h-full w-full object-cover opacity-70 pointer-events-none"
            muted
            playsInline
            preload="auto"
            crossOrigin="anonymous"
          />
        ) : (
          clip.thumbSmall && <img src={clip.thumbSmall} alt="" className="h-full w-full object-cover opacity-60" loading="lazy" />
        )}
        <div className="absolute inset-0 pointer-events-none" style={{ background: variant === 'splice' ? 'linear-gradient(180deg, transparent 70%, rgba(0,0,0,0.55) 100%)' : 'linear-gradient(180deg, transparent 40%, rgba(15,8,30,0.85) 100%)' }} />
      </div>
      <div className="absolute bottom-1 left-2 right-2 flex justify-between z-10 items-end gap-2">
        <span className="text-[10px] font-semibold text-white truncate">{clip.label}</span>
        <span className="text-[10px] font-mono font-bold text-black bg-white/90 px-1.5 py-[1px] rounded shrink-0 shadow-sm" data-testid={`clip-duration-pill-${clip.id}`}>
          {`:${clip.duration.toFixed(2).padStart(5, '0')}`}
        </span>
      </div>
      {/* LEFT trim handle (always visible so it works on touch devices) */}
      <div className="absolute left-0 top-0 bottom-0 w-5 cursor-col-resize z-20 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity touch-none"
        style={{ background: 'linear-gradient(90deg, rgba(244,63,94,0.85), rgba(244,63,94,0.25) 60%, transparent)' }}
        onMouseDown={(e) => startTrim(e, 'L')}
        onTouchStart={(e) => startTrim(e, 'L')}
        data-testid={`trim-left-${clip.id}`}
      >
        <div className="w-1 h-10 rounded-full bg-white" />
      </div>
      {/* RIGHT trim handle */}
      <div className="absolute right-0 top-0 bottom-0 w-5 cursor-col-resize z-20 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity touch-none"
        style={{ background: 'linear-gradient(-90deg, rgba(244,63,94,0.85), rgba(244,63,94,0.25) 60%, transparent)' }}
        onMouseDown={(e) => startTrim(e, 'R')}
        onTouchStart={(e) => startTrim(e, 'R')}
        data-testid={`trim-right-${clip.id}`}
      >
        <div className="w-1 h-10 rounded-full bg-white" />
      </div>
      {isSelected && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(clip.id); }}
          className="absolute top-1 right-5 p-0.5 rounded bg-red-500/80 hover:bg-red-500 z-20">
          <Trash2 className="w-2.5 h-2.5 text-white" />
        </button>
      )}
      {DEBUG && (
        <div
          className="absolute top-0 left-0 bg-black/80 text-[9px] text-yellow-300 font-mono px-1 py-0.5 leading-tight z-30 pointer-events-none"
          data-testid={`clip-debug-${clip.id}`}
        >
          <div>ts={(clip.trimStart ?? 0).toFixed(3)}</div>
          <div>te={(clip.trimEnd ?? clip.duration).toFixed(3)}</div>
          <div>od={(clip.originalDuration ?? 0).toFixed(3)}</div>
          <div>d={clip.duration.toFixed(3)}</div>
          <div>iL={(-(clip.trimStart || 0) * pps).toFixed(1)}</div>
          <div>iW={((clip.originalDuration || clip.duration) * pps).toFixed(1)}</div>
        </div>
      )}
    </div>
  );
}

export default function TimelineEditor({ shots = [], onExport, onExportPayload, onAddClips, onClipsChange, exportLabel, orientationOptions, onOrientationChange, shotsAvailable, baseUrl = '', onProbedurations, orientation = '16:9', variant = 'project', token = null, apiBase = null, initialDraft = null, onDraftChange = null, saveStatus = 'idle' }) {
  const [clips, setClips] = useState([]);
  const [audio, setAudio] = useState(null);

  // One-time hydration from a server-side draft (autosave restore).
  // Runs once as soon as `initialDraft` arrives from the parent.
  const draftHydratedRef = useRef(false);
  useEffect(() => {
    if (draftHydratedRef.current) return;
    if (!initialDraft || !Array.isArray(initialDraft.clips) || initialDraft.clips.length === 0) return;
    draftHydratedRef.current = true;
    // Re-repack startTime defensively in case the stored draft had stale offsets.
    let t = 0;
    const hydrated = initialDraft.clips.map(c => ({
      ...c,
      startTime: (t += (c.duration || 0)) - (c.duration || 0),
    }));
    setClips(hydrated);
    if (initialDraft.audio) setAudio(initialDraft.audio);
  }, [initialDraft]);
  const [pps, setPps] = useState(PPS_INITIAL);
  const [pos, setPos] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [selId, setSelId] = useState(null);
  const [videoTrackMuted, setVideoTrackMuted] = useState(false);  // mutes ALL clip audio on the Video track
  const [bgmMuted, setBgmMuted] = useState(false);        // mutes the background song on the Audio track
  const [vol, setVol] = useState(80);
  const [videoReady, setVideoReady] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [previewAll, setPreviewAll] = useState(false);
  const [transitionProgress, setTransitionProgress] = useState(0); // 0 = no transition, 0-1 = in transition
  const [nextClipThumb, setNextClipThumb] = useState(null);

  const previewAspect = orientation === '9:16' ? 'aspect-[9/16] max-w-xs'
    : orientation === '1:1' ? 'aspect-square max-w-md'
    : 'aspect-video max-w-xl';
  const vidRef = useRef(null);
  const preloadRef = useRef(null); // Preload next clip's video
  const preloadedSet = useRef(new Set());
  const audioElRef = useRef(null);
  const tlRef = useRef(null);
  const rafRef = useRef(null);
  const dragRef = useRef(false);
  const curClipIdRef = useRef(null);
  const playingRef = useRef(false);
  const posRef = useRef(0);
  const clipsRef = useRef([]);
  const audioInRef = useRef(null);
  const previewAllRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => { playingRef.current = playing; }, [playing]);
  useEffect(() => { posRef.current = pos; }, [pos]);
  useEffect(() => { clipsRef.current = clips; }, [clips]);
  useEffect(() => { previewAllRef.current = previewAll; }, [previewAll]);
  const videoReadyRef = useRef(false);
  useEffect(() => { videoReadyRef.current = videoReady; }, [videoReady]);

  // ─── Beat Sync (spec: BeatSync_Button_TechSpec_v1) ────────────────────
  // preSyncClipsRef snapshots the clips array on the FIRST apply in the
  // current session. Subsequent re-rolls do NOT overwrite it — this way Undo
  // always returns the user to the state before they started beat-syncing,
  // regardless of how many Try-Again taps they did. Ref is nulled on Undo so
  // a later Beat Sync tap captures a fresh snapshot.
  const preSyncClipsRef = useRef(null);
  const audioRef = useRef(null);
  useEffect(() => { audioRef.current = audio; }, [audio]);
  const [beatSyncing, setBeatSyncing] = useState(false);
  const beatSyncingRef = useRef(false);
  useEffect(() => { beatSyncingRef.current = beatSyncing; }, [beatSyncing]);

  // Build clips from shots — uses actual video duration when available
  const buildClips = useCallback((inputShots) => {
    let t = 0;
    return inputShots.map((s, i) => {
      // Prefer actual probed video duration > shot-level duration > default 5s
      const d = s.video_actual_duration || s.duration || 5;
      const kfUrl = s.keyframe_url ? (s.keyframe_url.startsWith('http') ? s.keyframe_url : `${baseUrl}${s.keyframe_url}`) : null;
      // Thumbnail: use S3 URL directly if available (no need for thumb service), else use local thumb endpoint
      const thumbUrl = s.keyframe_url?.startsWith('http')
        ? s.keyframe_url  // S3 URL — use directly
        : (s.keyframe_url && !s.keyframe_url.startsWith('http'))
          ? `${baseUrl}/api/thumb/${s.keyframe_url.replace('/api/storage/', '')}?w=160&h=90`
          : kfUrl;
      const c = {
        id: s.id || `c${i}`, label: s.shot_id || `Shot ${i+1}`,
        videoUrl: s.video_url 
          ? (s.video_url.startsWith('http') ? s.video_url : `${baseUrl}${s.video_url}`)
          : (s.video_s3_url 
            ? (s.video_s3_url.startsWith('http') ? s.video_s3_url : `${baseUrl}${s.video_s3_url}`)
            : null),
        videoS3Url: s.video_s3_url
          ? (s.video_s3_url.startsWith('http') ? s.video_s3_url : `${baseUrl}${s.video_s3_url}`)
          : null,
        thumbnailUrl: kfUrl,      // Full-res for preview
        thumbSmall: thumbUrl,     // Small thumbnail for timeline clip
        startTime: t, duration: d, originalDuration: d, trimStart: 0, trimEnd: d,
        volume: 100, muted: false,
      };
      t += d;
      return c;
    });
  }, [baseUrl]);

  // IDs of clips the user has explicitly deleted from the timeline. The parent
  // `shots` prop keeps a monotonically growing list of shots the user added to
  // the timeline library and never shrinks when clips are removed here. Without
  // this set, the "append new shots" effect below would see the deleted clip's
  // shot as "new" and re-add it, making the delete button appear broken.
  const removedIdsRef = useRef(new Set());

  // Init clips — and append new shots if added later
  useEffect(() => {
    if (shots.length > 0 && clips.length === 0 && !draftHydratedRef.current) {
      const filtered = shots.filter(s => !removedIdsRef.current.has(s.id));
      if (filtered.length > 0) setClips(buildClips(filtered));
    } else if (shots.length > 0) {
      // Append any new shots (by id) that aren't already on the timeline and
      // weren't previously removed by the user. Works even when clips were
      // hydrated from a draft.
      const existingIds = new Set(clips.map(c => c.id));
      const newShots = shots.filter(s => !existingIds.has(s.id) && !removedIdsRef.current.has(s.id));
      if (newShots.length > 0) {
        const newClips = buildClips(newShots);
        const offset = clips.reduce((t, c) => t + c.duration, 0);
        const appended = newClips.map((c, i) => ({
          ...c,
          startTime: offset + newClips.slice(0, i).reduce((t, x) => t + x.duration, 0),
        }));
        setClips([...clips, ...appended]);
      }
    }
  }, [shots, buildClips, clips.length]);

  // Auto-probe video durations on first mount
  const probedRef = useRef(false);
  useEffect(() => {
    if (!probedRef.current && onProbedurations && shots.some(s => s.video_status === 'completed' && !s.video_actual_duration)) {
      probedRef.current = true;
      onProbedurations();
    }
  }, [shots, onProbedurations]);

  // Rebuild clips when shots change (e.g., after duration probe)
  useEffect(() => {
    if (shots.length > 0 && clips.length > 0) {
      // Check if any duration changed
      const hasNewDurations = shots.some(s => {
        const existing = clips.find(c => c.id === s.id);
        const expected = s.video_actual_duration || s.duration || 5;
        return existing && Math.abs(existing.originalDuration - expected) > 0.1;
      });
      if (hasNewDurations) {
        setClips(buildClips(shots));
      }
    }
  }, [shots, buildClips]); // eslint-disable-line

  // Debounced autosave — commits the current timeline state to the server-side
  // draft 1500ms after the last change. Skips during a trim drag so we don't
  // flood with saves on every touchmove; also skips the very first render
  // before any interaction has occurred.
  const saveTimerRef = useRef(null);
  const saveSkipFirstRef = useRef(true);
  useEffect(() => {
    if (!onDraftChange) return;
    if (saveSkipFirstRef.current) { saveSkipFirstRef.current = false; return; }
    if (clips.length === 0) return;  // nothing worth saving
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (dragRef.current) return;  // user still scrubbing/trimming — retry next tick
      const total = clips.reduce((s, c) => Math.max(s, (c.startTime || 0) + c.duration), 0);
      try {
        onDraftChange({
          clips,
          audio,
          aspect_ratio: orientation,
          total_duration: total,
        });
      } catch (_) {}
    }, 1500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [clips, audio, orientation, onDraftChange]);

  // Splice-style yellow playhead for 'splice' variant, otherwise default cyan
  const playheadColor = variant === 'splice' ? '#FBBF24' : '#38BDF8';

  // Notify the parent panel whenever the live clips array changes. The parent
  // uses this to update UI state that depends on what's currently on the
  // timeline — e.g. the green "added" checkmark on Available Clips tiles,
  // which must disappear when the user removes a clip via Split/Delete.
  useEffect(() => {
    if (!onClipsChange) return;
    try { onClipsChange(clips); } catch (_) { /* ignore */ }
  }, [clips, onClipsChange]);

  const total = clips.reduce((s, c) => Math.max(s, (c.startTime || 0) + c.duration), 0);

  // Find current clip based on playhead position
  const findClipAtPos = useCallback((t, clipsList) => {
    let acc = 0;
    for (const c of clipsList) {
      if (t >= acc && t < acc + c.duration) return { ...c, localTime: t - acc + c.trimStart };
      acc += c.duration;
    }
    return clipsList[0] ? { ...clipsList[0], localTime: clipsList[0].trimStart } : null;
  }, []);

  // Find current clip — only update state when clip ID changes to avoid re-render storms
  const curClipRaw = useMemo(() => findClipAtPos(pos, clips), [findClipAtPos, pos, clips]);
  const [curClip, setCurClip] = useState(null);
  
  useEffect(() => {
    if (curClipRaw?.id !== curClip?.id || !curClip) {
      setCurClip(curClipRaw);
    } else if (curClipRaw) {
      // Same clip — just update localTime without triggering video reload
      curClip.localTime = curClipRaw.localTime;
    }
  }, [curClipRaw?.id, curClipRaw?.localTime]); // eslint-disable-line

  // Current clip index for Preview All mode
  const curClipIndex = useMemo(() => {
    let acc = 0;
    for (let i = 0; i < clips.length; i++) {
      if (pos >= acc && pos < acc + clips[i].duration) return i;
      acc += clips[i].duration;
    }
    return 0;
  }, [clips, pos]);

  // Lightweight preloader: only preload 1 clip ahead using link[rel=preload]
  useEffect(() => {
    if (clips.length === 0) return;
    const nextIdx = curClipIndex + 1;
    if (nextIdx >= clips.length) return;
    const nextClip = clips[nextIdx];
    const src = nextClip?.videoUrl || nextClip?.videoS3Url;
    if (!src || preloadedSet.current.has(src)) return;
    preloadedSet.current.add(src);
    
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = src;
    link.as = 'video';
    document.head.appendChild(link);
    
    return () => { try { document.head.removeChild(link); } catch {} };
  }, [clips, curClipIndex]);


  // Detect transition zone (last TRANSITION_DURATION seconds of each clip)
  useEffect(() => {
    if (!previewAll || !playing || clips.length < 2) {
      if (transitionProgress > 0) setTransitionProgress(0);
      return;
    }
    let acc = 0;
    for (let i = 0; i < clips.length; i++) {
      const clipEnd = acc + clips[i].duration;
      const transStart = clipEnd - TRANSITION_DURATION;
      if (pos >= transStart && pos < clipEnd && i < clips.length - 1) {
        const prog = (pos - transStart) / TRANSITION_DURATION;
        setTransitionProgress(Math.min(1, Math.max(0, prog)));
        setNextClipThumb(clips[i + 1]?.thumbnailUrl || null);
        return;
      }
      acc += clips[i].duration;
    }
    if (transitionProgress > 0) setTransitionProgress(0);
  }, [pos, previewAll, playing, clips, transitionProgress]);

  // Auto-scroll timeline to follow the playhead in ALL cases — manual scrubbing,
  // Preview All playback, and the imperative video-element `timeupdate` driven
  // `pos` ticks. Keeps the yellow bar visible when the clip is longer than the
  // viewport. Uses `behavior: auto` during scrubs (instant, feels locked to the
  // finger) and `smooth` during playback (nicer, no jitter).
  useEffect(() => {
    if (!tlRef.current) return;
    const scrollContainer = tlRef.current;
    const playheadX = pos * pps;
    const containerWidth = scrollContainer.clientWidth;
    const scrollLeft = scrollContainer.scrollLeft;
    // Trigger when playhead leaves a 10% margin on either side of the viewport.
    const rightEdge = scrollLeft + containerWidth * 0.9;
    const leftEdge = scrollLeft + containerWidth * 0.1;
    if (playheadX > rightEdge || playheadX < leftEdge) {
      scrollContainer.scrollTo({
        left: Math.max(0, playheadX - containerWidth * 0.5),
        behavior: playing ? 'smooth' : 'auto',
      });
    }
  }, [pos, playing, pps]);

  // Load video when current clip changes
  useEffect(() => {
    const v = vidRef.current;
    if (!v || !curClip) return;

    const videoSrc = curClip.videoUrl || curClip.videoS3Url;
    if (!videoSrc) {
      setVideoReady(false);
      setVideoError(null);
      return;
    }

    if (curClipIdRef.current !== curClip.id) {
      curClipIdRef.current = curClip.id;
      setVideoReady(false);
      setVideoError(null);

      // Remove old handlers
      v.onloadeddata = null;
      v.oncanplay = null;
      v.onerror = null;

      v.src = videoSrc;
      v.load();

      // Use onloadeddata (first frame available) instead of oncanplaythrough (full buffer)
      v.onloadeddata = () => {
        setVideoReady(true);
        v.currentTime = curClip.localTime || 0;
        if (playingRef.current) v.play().catch(() => {});
      };

      // Also try playing on canplay as backup
      v.oncanplay = () => {
        if (!videoReady) {
          setVideoReady(true);
          v.currentTime = curClip.localTime || 0;
          if (playingRef.current) v.play().catch(() => {});
        }
      };

      v.onerror = () => {
        // Try S3 fallback URL if available
        if (curClip.videoS3Url && v.src !== curClip.videoS3Url) {
          v.src = curClip.videoS3Url;
          v.load();
          return;
        }
        setVideoError(`Failed to load: ${curClip.label}`);
        setVideoReady(false);
      };
    } else if (videoReady && !playing) {
      // Same clip, just seek
      v.currentTime = curClip.localTime || 0;
    }
  }, [curClip?.id, curClip?.videoUrl]); // eslint-disable-line

  // Play/pause sync
  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    if (playing && videoReady) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [playing, videoReady]);

  // Audio sync
  useEffect(() => {
    const a = audioElRef.current;
    if (!a) return;
    if (playing) {
      // With `loop`, the BGM's currentTime must map to the timeline position
      // modulo the track's duration so the music stays in sync even when the
      // timeline is longer than the track and we've wrapped around.
      const audioDur = a.duration;
      const wrapped = (audioDur && isFinite(audioDur) && audioDur > 0)
        ? (posRef.current % audioDur)
        : posRef.current;
      a.currentTime = Math.max(0, wrapped);
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  }, [playing]);

  // Volume — per-track Mute icons (Video / Audio) + master volume slider.
  // - `videoTrackMuted` mutes all clip video audio (plus per-clip .muted still cascades)
  // - `bgmMuted` mutes the background music track only
  // - `vol` is a master slider that scales both tracks proportionally
  useEffect(() => {
    const clipMuted = !!curClip?.muted;
    const clipVol = typeof curClip?.volume === 'number' ? curClip.volume / 100 : 1;
    const a = audioElRef.current;
    const wasAudioPlaying = a && !a.paused && !a.ended;
    if (vidRef.current) {
      vidRef.current.muted = videoTrackMuted || clipMuted;
      vidRef.current.volume = Math.max(0, Math.min(1, (vol / 100) * clipVol));
    }
    if (a) {
      a.muted = bgmMuted;
      a.volume = vol / 100;
      // iOS Safari sometimes pauses the <audio> element when a concurrent
      // <video>'s muted state changes (shared audio session). If BGM was
      // playing before this effect ran and got silently paused, resume it.
      if (wasAudioPlaying && a.paused && playingRef.current && !bgmMuted) {
        a.play().catch(() => {});
      }
    }
  }, [videoTrackMuted, bgmMuted, vol, curClip?.id, curClip?.muted, curClip?.volume]);

  // Playhead tick — pauses when video is buffering on a new clip
  useEffect(() => {
    if (!playing) return;
    const totalDur = clips.reduce((s, c) => s + c.duration, 0);
    if (totalDur <= 0) return;
    
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      
      setPos(p => {
        // If video isn't ready, hold position (wait for buffer)
        if (!videoReadyRef.current) {
          return p;
        }
        
        const n = p + dt;
        if (n >= totalDur) { 
          setPlaying(false);
          if (previewAllRef.current) setPreviewAll(false);
          return 0; 
        }
        return n;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [playing, clips]);

  const trimStart = useCallback((id, val) => {
    logTrim('trimStart', id, val);
    setClips(prev => {
      const upd = prev.map(c => c.id !== id ? c : { ...c, trimStart: val, duration: Math.max(0.5, c.trimEnd - val) });
      // Ripple: repack all startTimes so clips snug up with no gaps
      let t = 0;
      return upd.map(c => { const r = { ...c, startTime: t }; t += c.duration; return r; });
    });
  }, []);

  const trimEnd = useCallback((id, val) => {
    logTrim('trimEnd', id, val);
    setClips(prev => {
      const upd = prev.map(c => c.id !== id ? c : { ...c, trimEnd: val, duration: Math.max(0.5, val - c.trimStart) });
      let t = 0;
      return upd.map(c => { const r = { ...c, startTime: t }; t += c.duration; return r; });
    });
  }, []);

  const removeClip = useCallback((id) => {
    // Record the deletion so the `shots`-watcher effect doesn't re-add this clip
    // on the next render (the parent's shots list never shrinks).
    removedIdsRef.current.add(id);
    setClips(prev => {
      const f = prev.filter(c => c.id !== id);
      let t = 0; return f.map(c => ({ ...c, startTime: (t += c.duration) - c.duration }));
    });
    setSelId(null);
  }, []);

  // Split the selected clip at the current playhead position.
  // The part before the playhead keeps the original id; the part after is a new clip with a fresh id
  // inserted immediately after. Both inherit volume/muted settings. If the playhead is outside
  // the clip, or too close to either edge (<0.2s), this is a no-op.
  const splitClipAtPlayhead = useCallback((id) => {
    setClips(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx < 0) return prev;
      const c = prev[idx];
      const localCut = posRef.current - c.startTime;  // position within clip's visible window
      const EDGE = 0.2;
      if (localCut < EDGE || localCut > c.duration - EDGE) return prev;
      const cutAbs = (c.trimStart || 0) + localCut;
      const first = { ...c, trimEnd: cutAbs, duration: cutAbs - (c.trimStart || 0) };
      const second = {
        ...c,
        id: `${c.id}-s${Date.now().toString(36).slice(-4)}`,
        trimStart: cutAbs,
        duration: (c.trimEnd || c.originalDuration || c.duration) - cutAbs,
      };
      const next = [...prev.slice(0, idx), first, second, ...prev.slice(idx + 1)];
      let t = 0; return next.map(x => ({ ...x, startTime: (t += x.duration) - x.duration }));
    });
  }, []);

  const duplicateClip = useCallback((id) => {
    setClips(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx < 0) return prev;
      const c = prev[idx];
      const dup = { ...c, id: `${c.id}-d${Date.now().toString(36).slice(-4)}` };
      const next = [...prev.slice(0, idx + 1), dup, ...prev.slice(idx + 1)];
      let t = 0; return next.map(x => ({ ...x, startTime: (t += x.duration) - x.duration }));
    });
  }, []);

  const toggleClipMute = useCallback((id) => {
    setClips(prev => prev.map(c => c.id !== id ? c : { ...c, muted: !c.muted }));
  }, []);

  const setClipVolume = useCallback((id, val) => {
    const v = Math.max(0, Math.min(200, Math.round(val)));
    setClips(prev => prev.map(c => c.id !== id ? c : { ...c, volume: v, muted: v === 0 ? true : c.muted }));
  }, []);

  const moveClip = (id, dir) => {
    setClips(prev => {
      const i = prev.findIndex(c => c.id === id);
      if (i < 0) return prev;
      const j = dir === 'left' ? i-1 : i+1;
      if (j < 0 || j >= prev.length) return prev;
      const a = [...prev]; [a[i], a[j]] = [a[j], a[i]];
      let t = 0; return a.map(c => ({ ...c, startTime: (t += c.duration) - c.duration }));
    });
  };

  const seekTo = useCallback((t) => {
    setPos(t);
    setPlaying(false);
    const v = vidRef.current;
    if (!v) return;
    const cl = findClipAtPos(t, clipsRef.current);
    if (!cl?.videoUrl && !cl?.videoS3Url) return;
    const videoSrc = cl.videoUrl || cl.videoS3Url;
    if (curClipIdRef.current !== cl.id) {
      curClipIdRef.current = cl.id;
      setVideoReady(false);
      // Clear stale handlers first so callbacks don't stomp each other.
      v.onloadeddata = null; v.oncanplay = null; v.oncanplaythrough = null;
      v.src = videoSrc;
      v.load();
      // Use onloadeddata (first frame ready) — matches the primary clip-load
      // path and avoids oncanplaythrough which is unreliable on desktop Chrome.
      v.onloadeddata = () => {
        setVideoReady(true);
        try { v.currentTime = cl.localTime || 0; } catch (_) {}
      };
      v.oncanplay = () => { if (!videoReadyRef.current) setVideoReady(true); };
      v.onerror = () => {
        if (cl.videoS3Url && v.src !== cl.videoS3Url) {
          v.src = cl.videoS3Url;
          v.load();
          return;
        }
      };
    } else {
      v.currentTime = cl.localTime || 0;
    }
    if (audioElRef.current) {
      const audioDur = audioElRef.current.duration;
      audioElRef.current.currentTime = (audioDur && isFinite(audioDur) && audioDur > 0)
        ? Math.max(0, t % audioDur)
        : Math.max(0, t);
    }
  }, [findClipAtPos]);

  const tlClick = (e) => {
    if (!tlRef.current || dragRef.current) return;
    const rect = tlRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + tlRef.current.scrollLeft;
    seekTo(Math.max(0, Math.min(x / pps, total)));
    setSelId(null);
  };

  // ─── Beat Sync handlers ────────────────────────────────────────────────
  // Defined as plain (non-memoised) functions so `clipsRef.current` and
  // `audioRef.current` always read the latest state — toast action callbacks
  // captured from an earlier render still Do The Right Thing.
  const undoBeatSync = () => {
    const snap = preSyncClipsRef.current;
    if (!snap) {
      toast.message('Nothing to undo.');
      return;
    }
    // Reset playhead to keep pos within the restored duration.
    setPos(0);
    setSelId(null);
    setClips(snap);
    preSyncClipsRef.current = null;
    toast.message('Reverted to pre-sync timeline.');
  };

  const applyBeatSync = async () => {
    if (beatSyncingRef.current) return;           // prevent double-tap
    const curClips = clipsRef.current || [];
    const curAudio = audioRef.current;
    if (curClips.length < 2) {
      toast.error('Beat Sync needs at least 2 clips on the timeline.');
      return;
    }
    if (!curAudio?.url) {
      toast.error('Add music first to enable Beat Sync.');
      return;
    }
    // Spec 3.2: deep-clone snapshot, taken ONLY on the first apply.
    if (preSyncClipsRef.current === null) {
      try {
        preSyncClipsRef.current = JSON.parse(JSON.stringify(curClips));
      } catch (_) {
        preSyncClipsRef.current = curClips.map(c => ({ ...c }));
      }
    }
    setBeatSyncing(true);
    const seed = Math.floor(Math.random() * 1_000_000_000);
    try {
      const base = apiBase || '';
      const resp = await fetch(`${base}/api/recipes/timeline/beat-sync-apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          clips: curClips,
          audio_url: curAudio.url,
          shuffle_seed: seed,
        }),
      });
      if (!resp.ok) {
        let detail = null;
        let rawText = '';
        try {
          rawText = await resp.text();
          if (rawText) {
            try { detail = JSON.parse(rawText); } catch (_) { /* non-JSON body */ }
          }
        } catch (_) { /* ignore */ }

        // FastAPI shape: { detail: { error, code } } OR { detail: "string" }
        // Be defensive — any of these could appear depending on proxy / middleware.
        const unwrap = detail?.detail ?? detail;
        const msgCandidates = [
          typeof unwrap === 'string' ? unwrap : null,
          unwrap?.error,
          unwrap?.message,
          unwrap?.detail,
          detail?.error,
          detail?.message,
          // Last resort: show whatever JSON/text came back so it's never silent.
          rawText && rawText.length < 300 ? rawText : null,
        ];
        const msg = msgCandidates.find(m => typeof m === 'string' && m.trim())
          || `Beat sync failed (HTTP ${resp.status}). Try again.`;
        const code = (unwrap && typeof unwrap === 'object') ? (unwrap.code || 'unknown') : 'unknown';
        // eslint-disable-next-line no-console
        console.warn('[BeatSync] HTTP', resp.status, 'code=', code, 'msg=', msg, 'detail=', detail, 'raw=', rawText?.slice(0, 200));
        toast.error(msg);
        return;
      }
      const data = await resp.json();
      const newClips = Array.isArray(data.clips) ? data.clips : null;
      if (!newClips) {
        toast.error('Beat sync returned no clips. Try again.');
        return;
      }
      // eslint-disable-next-line no-console
      console.log('[BeatSync] apply ok', { seed, tempo: data.tempo, cuts_used: data.cuts_used, warnings: data.warnings });
      setClips(newClips);
      setPos(0);
      toast('Synced to beat', {
        description: Array.isArray(data.warnings) && data.warnings.length
          ? `${data.warnings.length} clip(s) clamped to source length.`
          : `Tempo ${Math.round(data.tempo || 0)} BPM · ${(data.cuts_used || []).length} cuts on beat.`,
        duration: 6000,
        action: {
          label: 'Undo',
          onClick: () => undoBeatSync(),
        },
        cancel: {
          label: 'Try again',
          onClick: () => applyBeatSync(),
        },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[BeatSync] request failed', e);
      toast.error('Beat sync failed. Try again.');
    } finally {
      setBeatSyncing(false);
    }
  };

  const reset = () => {
    setPlaying(false);
    setPreviewAll(false);
    setTransitionProgress(0);
    setClips(buildClips(shots));
    setPos(0); setSelId(null);
    curClipIdRef.current = null;
  };

  const startPreviewAll = () => {
    setSelId(null);
    setPos(0);
    setPreviewAll(true);
    setTransitionProgress(0);
    curClipIdRef.current = null;
    // Kick play() synchronously inside the user gesture (desktop Chrome requires this).
    const v = vidRef.current;
    const a = audioElRef.current;
    setPlaying(true);
    if (v) { try { v.play().catch(() => {}); } catch (_) {} }
    if (a) { try { a.play().catch(() => {}); } catch (_) {} }
  };

  const stopPreviewAll = () => {
    setPlaying(false);
    setPreviewAll(false);
    setTransitionProgress(0);
  };

  const selClip = clips.find(c => c.id === selId);
  // The "action target" — prefer an explicitly selected clip, fall back to whichever
  // clip the playhead is currently over. Lets the bottom toolbar (Split/Mute/Duplicate/
  // Delete) operate directly on the clip under the yellow bar without an extra tap.
  const targetClip = selClip || curClipRaw;

  // Track horizontal scroll of timeline track so the floating clip-action menu
  // stays aligned with the selected clip as the user scrubs the ruler.
  const [tlScrollLeft, setTlScrollLeft] = useState(0);
  useEffect(() => {
    const el = tlRef.current;
    if (!el) return;
    const handler = () => setTlScrollLeft(el.scrollLeft || 0);
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, []);

  return (
    <div className="flex flex-col h-full" style={{ background: 'rgba(15,8,30,0.8)' }} data-testid="timeline-editor">
      
      {/* Preview + Inspector row */}
      <div className="flex flex-1 min-h-0">

        {/* Video Preview — full width now that the Inspector panel is removed.
            Clip trimming is available via the edge drag-handles on each clip
            on the timeline, and Remove / Mute / Duplicate / Split live in the
            per-clip action toolbar below. */}
        <div className="flex-1 flex flex-col items-center justify-center p-3" style={{ background: 'rgba(10,5,20,0.95)' }}>
          <div className={`relative w-full ${previewAspect} rounded-lg overflow-hidden bg-black mb-3`}
            data-testid="timeline-video-preview"
            onClick={() => {
              const v = vidRef.current;
              const a = audioElRef.current;
              const willPlay = !playing;
              setPlaying(willPlay);
              // Kick off play() SYNCHRONOUSLY inside the user-gesture scope so
              // desktop Chrome (strict autoplay policy) allows it. Without this,
              // the async useEffect-driven play() is rejected with NotAllowedError
              // and the preview freezes on the first frame.
              if (willPlay && v) {
                try { v.play().catch(() => {}); } catch (_) {}
                if (a) { try { a.play().catch(() => {}); } catch (_) {} }
              }
            }}
            style={{ cursor: 'pointer' }}>
            
            {/* Single persistent video element — never unmounted.
                `muted` is controlled imperatively by the volume useEffect below
                (NOT as a JSX prop) — setting it both ways on iOS Safari can
                perturb a concurrently-playing <audio> element's audio session
                and silently stop BGM playback. */}
            <video ref={vidRef} className="w-full h-full object-contain" playsInline
              preload="auto"
              style={{ display: videoReady && !videoError ? 'block' : 'none' }} />
            
            {/* Thumbnail: shown when video is loading, has error, or has no URL */}
            {(!videoReady || videoError || !(curClip?.videoUrl || curClip?.videoS3Url)) && curClip?.thumbnailUrl && (
              <img src={curClip.thumbnailUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
            )}
            {!curClip && (
              <div className="w-full h-full flex items-center justify-center"><Film className="w-12 h-12 text-white/20" /></div>
            )}

            {/* Loading / Buffering indicator */}
            {(curClip?.videoUrl || curClip?.videoS3Url) && !videoReady && !videoError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
                <div className="w-8 h-8 border-2 border-ai-accent border-t-transparent rounded-full animate-spin mb-2" />
                {playing && <span className="text-[10px] text-white/70 font-mono">BUFFERING...</span>}
              </div>
            )}

            {/* Error indicator */}
            {videoError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60">
                <AlertTriangle className="w-8 h-8 text-yellow-400 mb-2" />
                <p className="text-xs text-white/60">{videoError}</p>
              </div>
            )}

            {/* Overlays */}
            {variant === 'splice' ? (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-md text-[13px] font-mono font-semibold text-white bg-black/70 tracking-wider tabular-nums" data-testid="splice-time-readout">
                <span className="text-yellow-300">{fmt(pos).replace(/(\d\d):(\d\d):(\d\d)/, (_, m, s, f) => `${Number(m)}:${s}.${Math.floor(Number(f)/3)}`)}</span>
                <span className="text-white/40 mx-1.5">/</span>
                <span className="text-white/70">{fmt(total).replace(/(\d\d):(\d\d):(\d\d)/, (_, m, s) => `${Number(m)}:${s}`)}</span>
              </div>
            ) : (
              <>
                <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded text-[10px] font-mono text-white bg-black/70">{fmt(pos)}</div>
                <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded text-[10px] font-mono text-white/50 bg-black/70">{fmt(total)}</div>
              </>
            )}
            {curClip && <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-bold text-white bg-ai-accent/80">{curClip.label}</div>}
            
            {/* Preview All badge & clip counter */}
            {previewAll && playing && (
              <div className="absolute top-2 right-2 flex items-center gap-2">
                <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold text-white bg-gradient-to-r from-[#0EA5E9] to-[#14B8A6]">
                  <Clapperboard className="w-3 h-3" />PREVIEW
                </div>
                <div className="px-2 py-0.5 rounded text-[9px] font-mono text-white/80 bg-black/70">
                  {curClipIndex + 1}/{clips.length}
                </div>
              </div>
            )}
            {!previewAll && playing && <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold text-red-400 bg-black/70"><div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />LIVE</div>}

            {/* Crossfade transition overlay */}
            {previewAll && transitionProgress > 0 && nextClipThumb && (
              <div className="absolute inset-0 z-10 pointer-events-none" style={{ opacity: transitionProgress }}>
                <img src={nextClipThumb} alt="" className="w-full h-full object-cover" />
                {/* Purple sweep line */}
                <div className="absolute top-0 bottom-0 w-1" 
                  style={{ 
                    left: `${(1 - transitionProgress) * 100}%`,
                    background: 'linear-gradient(180deg, #0EA5E9, #38BDF8, #0EA5E9)',
                    boxShadow: '0 0 20px rgba(139,92,246,0.8), 0 0 40px rgba(139,92,246,0.4)',
                  }} />
              </div>
            )}

            {!playing && videoReady && !previewAll && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Play className="w-12 h-12 text-white/30" /></div>}
          </div>

          {/* Transport — centered, with tactile "pressed" feedback on tap */}
          <div className="flex items-center justify-center gap-3">
            <button onClick={() => seekTo(0)}
              className="p-2 rounded-lg hover:bg-white/10 active:bg-white/20 active:scale-95 transition-transform"
              data-testid="timeline-skip-back"><SkipBack className="w-4 h-4 text-white/70" /></button>
            <button onClick={() => {
              if (previewAll) { stopPreviewAll(); return; }
              const v = vidRef.current;
              const a = audioElRef.current;
              const willPlay = !playing;
              setPlaying(willPlay);
              if (willPlay && v) {
                try { v.play().catch(() => {}); } catch (_) {}
                if (a) { try { a.play().catch(() => {}); } catch (_) {} }
              }
            }} data-testid="timeline-play-btn"
              className="p-3 rounded-full active:scale-90 transition-transform"
              style={{ background: playing ? 'linear-gradient(135deg,#EF4444,#DC2626)' : 'linear-gradient(135deg,#0EA5E9,#38BDF8)' }}>
              {playing ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
            </button>
            <button onClick={() => seekTo(total)}
              className="p-2 rounded-lg hover:bg-white/10 active:bg-white/20 active:scale-95 transition-transform"
              data-testid="timeline-skip-forward"><SkipForward className="w-4 h-4 text-white/70" /></button>
          </div>
        </div>
      </div>

      {/* Audio element */}
      {/* Background music. `loop` so a short track fills the full timeline
          (common on iOS where gallery-extracted audio is often ~15s but the
          video timeline is 30–60s — otherwise users hear silence for the
          back half and think BGM is broken). */}
      {audio && <audio ref={audioElRef} src={audio.url} preload="auto" loop />}

      {/* Timeline */}
      <div className="border-t border-ai-border" style={{ background: 'rgba(15,8,30,0.95)' }}>
        {/* Toolbar row — single row, horizontally scrollable on narrow mobile.
            Keeps Zoom / Replace / Beat Sync / Export MP4 visually grouped on
            one line instead of wrapping haphazardly. */}
        <div
          className="flex flex-nowrap items-center justify-between gap-3 px-4 py-1.5 border-b border-ai-border overflow-x-auto"
          style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
        >
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[10px] font-bold text-white/60 uppercase tracking-wider">Timeline</span>
            <button onClick={reset} className="p-1 hover:bg-white/10 rounded" title="Reset" data-testid="timeline-reset"><RotateCcw className="w-3.5 h-3.5 text-white/60" /></button>
            {/* Autosave indicator — only visible when an owning parent is tracking save state */}
            {onDraftChange && saveStatus !== 'idle' && (
              <span
                className="flex items-center gap-1 text-[10px] text-zinc-500 ml-1"
                data-testid="timeline-save-status"
                title={
                  saveStatus === 'saving' ? 'Saving timeline…' :
                  saveStatus === 'saved'  ? 'Timeline saved' :
                  saveStatus === 'error'  ? 'Save failed' : ''
                }
              >
                {saveStatus === 'saving' && (<><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>)}
                {saveStatus === 'saved'  && (<><Check className="w-3 h-3 text-green-500/70" /> Saved</>)}
                {saveStatus === 'error'  && (<span className="text-amber-400/80">Save failed</span>)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-nowrap">
            <button onClick={() => setPps(p => Math.max(PPS_MIN, p-20))} className="p-1 hover:bg-white/10 rounded" data-testid="timeline-zoom-out"><ZoomOut className="w-3.5 h-3.5 text-white/60" /></button>
            <span className="text-[10px] text-white/50 w-10 text-center">{Math.round(pps/PPS_DEFAULT*100)}%</span>
            <button onClick={() => setPps(p => Math.min(PPS_MAX, p+20))} className="p-1 hover:bg-white/10 rounded" data-testid="timeline-zoom-in"><ZoomIn className="w-3.5 h-3.5 text-white/60" /></button>
            <div className="w-px h-4 bg-ai-accent/15" />
            <input ref={audioInRef} type="file" accept="audio/*,video/*" className="hidden" onChange={async e => {
              const f = e.target.files?.[0];
              if (e.target) e.target.value = '';
              if (!f) return;
              // Instant local playback via blob URL; we swap in the server URL
              // once the upload completes so the backend can fetch it during
              // export / beat-sync (the blob: scheme is browser-local only).
              const blobUrl = URL.createObjectURL(f);
              const tmp = new Audio(blobUrl);
              tmp.onloadedmetadata = async () => {
                const dur = tmp.duration;
                setAudio({ name: f.name, url: blobUrl, duration: dur });
                try {
                  const fd = new FormData();
                  fd.append('file', f);
                  const base = apiBase || '';
                  const resp = await fetch(`${base}/api/recipes/uploads`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: fd,
                  });
                  if (resp.ok) {
                    const data = await resp.json();
                    const rawUrl = data.url || data.file_url;
                    if (rawUrl) {
                      // The /api/recipes/storage/ endpoint requires auth. The
                      // <audio> tag can't send Authorization headers, so mirror
                      // MontageTimelinePanel's withAuthToken helper by
                      // appending a ?token=… query string. Without this the
                      // audio element 401s, stays at readyState=0, and
                      // .play() silently fails → no music audible.
                      const full = rawUrl.startsWith('http') ? rawUrl : `${base}${rawUrl}`;
                      const sep = full.includes('?') ? '&' : '?';
                      const withToken = `${full}${sep}token=${encodeURIComponent(token)}`;
                      setAudio({ name: f.name, url: withToken, duration: dur });
                    }
                  }
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.warn('[audio upload] failed, using local blob URL only', err);
                }
              };
            }} />
            <button onClick={() => audioInRef.current?.click()} data-testid="timeline-add-audio"
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] text-white/70 hover:bg-white/10 border border-ai-border whitespace-nowrap active:scale-95 active:bg-white/15 transition-transform">
              <Music className="w-3 h-3" /> {audio ? 'Replace' : 'Add Audio'}
            </button>
            <button
              onClick={applyBeatSync}
              disabled={!audio?.url || beatSyncing || clips.length < 2}
              data-testid="timeline-beat-sync-btn"
              title={!audio?.url
                ? 'Add music first to enable Beat Sync.'
                : clips.length < 2
                  ? 'Beat Sync needs at least 2 clips on the timeline.'
                  : 'Align clip cuts to music beats'}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold border transition-all whitespace-nowrap active:scale-95 ${
                !audio?.url || clips.length < 2
                  ? 'text-white/30 border-ai-border cursor-not-allowed'
                  : beatSyncing
                    ? 'text-white border-transparent cursor-wait'
                    : 'text-white border-transparent hover:brightness-110 active:brightness-90'
              }`}
              style={(!audio?.url || clips.length < 2) ? undefined : {
                background: 'linear-gradient(135deg,#8B5CF6,#EC4899)',
              }}>
              {beatSyncing
                ? (<><Loader2 className="w-3 h-3 animate-spin" /> Syncing…</>)
                : (<><Zap className="w-3 h-3" /> Beat Sync</>)}
            </button>
            <button onClick={() => {
              if (onExportPayload) {
                // Build payload from current clip state for the consumer.
                // Important: `videoTrackMuted` is a UI-only toggle used for
                // preview — the backend export has no concept of a "track
                // mute", only per-clip `muted`. Propagate the track mute
                // down into every clip so the rendered MP4 matches what the
                // user heard in preview (BGM only, no clip audio).
                const payload = {
                  clips: clips.map((c, i) => ({
                    url: c.videoUrl || c.videoS3Url,
                    in: Number((c.trimStart || 0).toFixed(3)),
                    out: Number(((c.trimStart || 0) + (c.duration || 0)).toFixed(3)),
                    transition_out: c.transition_out || 'cut',
                    volume: typeof c.volume === 'number' ? c.volume : 100,
                    muted: !!c.muted || videoTrackMuted,
                  })),
                  audio_url: (audio?.url && !bgmMuted) ? audio.url : null,
                  total_duration: Number((clips.reduce((s, c) => s + c.duration, 0)).toFixed(3)),
                };
                onExportPayload(payload);
              } else {
                onExport && onExport();
              }
            }} data-testid="timeline-export-btn"
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold text-white whitespace-nowrap transition-all active:scale-95 active:brightness-90 hover:brightness-110"
              style={{ background: 'linear-gradient(135deg,#0EA5E9,#38BDF8)' }}>
              <Download className="w-3 h-3" /> {exportLabel || 'Export'}
            </button>
          </div>
        </div>

        <div className="flex" style={{ position: 'relative' }}>
          <div className="w-24 flex-shrink-0 border-r border-ai-border">
            <div className="h-6" />
            <div className="flex items-center gap-1.5 px-2" style={{ height: TRACK_H, borderBottom: '1px solid rgba(139,92,246,0.1)' }}>
              <Film className="w-3 h-3 text-ai-accent flex-shrink-0" />
              <span className="text-[10px] font-semibold text-white/70 flex-1">Video</span>
              <button
                onClick={() => setVideoTrackMuted(v => !v)}
                title={videoTrackMuted ? 'Unmute clip audio (video track)' : 'Mute clip audio (video track only)'}
                data-testid="timeline-video-track-mute"
                className={`p-1 rounded hover:bg-white/10 transition-colors ${videoTrackMuted ? 'text-rose-400' : 'text-white/60'}`}>
                {videoTrackMuted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
              </button>
            </div>
            {audio && (
              <div className="flex items-center gap-1.5 px-2" style={{ height: 36, borderBottom: '1px solid rgba(139,92,246,0.1)' }}>
                <Music className="w-3 h-3 text-green-400 flex-shrink-0" />
                <span className="text-[10px] font-semibold text-white/70 flex-1">Audio</span>
                <button
                  onClick={() => setBgmMuted(v => !v)}
                  title={bgmMuted ? 'Unmute background music' : 'Mute background music (clips keep playing)'}
                  data-testid="timeline-bgm-track-mute"
                  className={`p-1 rounded hover:bg-white/10 transition-colors ${bgmMuted ? 'text-rose-400' : 'text-green-400/70'}`}>
                  {bgmMuted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                </button>
              </div>
            )}
          </div>

          <div ref={tlRef} className="flex-1 overflow-x-auto custom-scrollbar" data-testid="timeline-track-area">
            <Ruler total={total} pps={pps} pos={pos} onScrub={seekTo} playheadColor={playheadColor} />
            <div
              className="relative"
              style={{ height: TRACK_H, width: Math.max(total * pps, 600), borderBottom: '1px solid rgba(139,92,246,0.1)', background: 'rgba(139,92,246,0.03)', touchAction: 'pan-x' }}
            >
              {clips.map((c, idx) => (
                <Clip key={c.id} clip={c} pps={pps} isSelected={selId === c.id}
                  isPlaying={previewAll && playing && idx === curClipIndex}
                  onSelect={setSelId} onSeekTap={seekTo} onRemove={removeClip}
                  onTrimStart={trimStart} onTrimEnd={trimEnd}
                  scrubTime={pos} variant={variant} token={token} apiBase={apiBase}
                  onDragStart={() => { dragRef.current = true; }}
                  onDragEnd={() => { dragRef.current = false; }} />
              ))}
              <div className="absolute top-0 bottom-0 z-30 pointer-events-none" style={{
                left: pos * pps - (variant === 'splice' ? 1 : 0.5),
                width: variant === 'splice' ? 2 : 1,
                background: playheadColor,
                boxShadow: variant === 'splice' ? `0 0 6px ${playheadColor}, 0 0 14px ${playheadColor}90` : undefined,
              }} />
            </div>
            {audio && (
              <div className="relative" style={{ height: 36, width: Math.max(total * pps, 600), background: 'rgba(34,197,94,0.03)' }}>
                <div className="absolute top-1 bottom-1 rounded-lg overflow-hidden" style={{ width: Math.min(audio.duration||total, total) * pps, background: 'linear-gradient(135deg,rgba(34,197,94,0.3),rgba(34,197,94,0.15))', border: '1px solid rgba(34,197,94,0.3)' }}>
                  <div className="absolute bottom-0.5 left-2 text-[8px] text-green-300/70 truncate">{audio.name}</div>
                  <div className="flex items-center h-full px-0.5 gap-px">
                    {Array.from({ length: Math.min(120, Math.floor((audio.duration||30)*3)) }).map((_, i) => (
                      <div key={i} className="flex-1 rounded-full" style={{ height: `${20+Math.sin(i*0.5)*25+Math.random()*20}%`, background: 'rgba(34,197,94,0.5)', minWidth: 1 }} />
                    ))}
                  </div>
                </div>
                <div className="absolute top-0 bottom-0 z-30 pointer-events-none" style={{
                  left: pos * pps - (variant === 'splice' ? 1 : 0.5),
                  width: variant === 'splice' ? 2 : 1,
                  background: playheadColor,
                  boxShadow: variant === 'splice' ? `0 0 6px ${playheadColor}` : undefined,
                }} />
              </div>
            )}
          </div>
        </div>

        {/* Splice-style bottom toolbar — always visible; disabled only when timeline is empty.
            Prefers selected clip; falls back to the clip under the playhead so Split/Mute/
            Duplicate/Delete all operate directly on whatever the yellow scrub bar is over. */}
        {variant === 'splice' && (
          <div
            className="flex flex-nowrap items-center gap-3 px-4 py-2 border-t border-ai-border overflow-x-auto"
            style={{ background: 'rgba(10,6,20,0.95)', scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
            data-testid="clip-bottom-toolbar"
          >
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Film className={`w-3 h-3 ${targetClip ? 'text-ai-accent' : 'text-white/30'}`} />
              <span className={`text-[10px] font-semibold truncate max-w-[90px] ${targetClip ? 'text-white/70' : 'text-white/40 italic'}`}>
                {targetClip ? targetClip.label : 'Select a clip'}
              </span>
              {!selClip && targetClip && (
                <span className="text-[9px] text-ai-accent/70 italic">@playhead</span>
              )}
            </div>
            <div className="flex items-center gap-2 max-w-[180px] flex-shrink-0">
              <button
                onClick={() => targetClip && toggleClipMute(targetClip.id)}
                disabled={!targetClip}
                className="p-1 rounded hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                title={targetClip?.muted ? 'Unmute' : 'Mute'}
                data-testid="clip-toolbar-mute"
              >
                {targetClip?.muted ? <VolumeX className="w-3.5 h-3.5 text-rose-300" /> : <Volume2 className="w-3.5 h-3.5 text-white/80" />}
              </button>
              <input
                type="range" min={0} max={200} step={5}
                value={targetClip ? (targetClip.muted ? 0 : (targetClip.volume ?? 100)) : 100}
                onChange={(e) => targetClip && setClipVolume(targetClip.id, Number(e.target.value))}
                className="flex-1 accent-ai-accent disabled:opacity-40"
                disabled={!targetClip || targetClip.muted}
                data-testid="clip-toolbar-volume"
              />
              <span className="text-[10px] font-mono text-white/60 w-9 text-right">
                {targetClip ? (targetClip.muted ? 'OFF' : `${targetClip.volume ?? 100}%`) : '—'}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => targetClip && splitClipAtPlayhead(targetClip.id)}
                disabled={!targetClip}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-white/80 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Split at playhead"
                data-testid="clip-toolbar-split"
              >
                <Scissors className="w-3 h-3" /> <span className="hidden sm:inline">Split</span>
              </button>
              <button
                onClick={() => targetClip && toggleClipMute(targetClip.id)}
                disabled={!targetClip}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed ${targetClip?.muted ? 'text-rose-300 bg-rose-500/10' : 'text-white/80'}`}
                title={targetClip?.muted ? 'Unmute clip audio' : 'Mute clip audio (keep added music only)'}
                data-testid="clip-toolbar-mute-btn"
              >
                {targetClip?.muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                <span className="hidden sm:inline">{targetClip?.muted ? 'Muted' : 'Mute'}</span>
              </button>
              <button
                onClick={() => targetClip && duplicateClip(targetClip.id)}
                disabled={!targetClip}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-white/80 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Duplicate"
                data-testid="clip-toolbar-duplicate"
              >
                <Copy className="w-3 h-3" /> <span className="hidden sm:inline">Duplicate</span>
              </button>
              <button
                onClick={() => targetClip && removeClip(targetClip.id)}
                disabled={!targetClip}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-rose-300 hover:bg-rose-500/15 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Delete"
                data-testid="clip-toolbar-delete"
              >
                <Trash2 className="w-3 h-3" /> <span className="hidden sm:inline">Delete</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
